import { useState, useRef, useEffect } from "react";
import { Send, Paperclip, Phone, Video, MoreHorizontal, CheckCheck, Clock, FileText, CalendarPlus, Star } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import { MOCK_MESSAGES, ChatMessage } from "../../data/mockData";

const COUNSELOR = {
  name: "Sarah Izzati Rahmat",
  nickname: "Kak Sarah",
  title: "Senior Education Counselor",
  specialization: "Medical & STEM Programmes",
  experience: "8 years experience",
  universities: "120+ students placed",
  rating: 4.9,
  responseTime: "~2 min avg.",
  avatar: "👩‍💼",
};

const QUICK_REPLIES = [
  "What's my application status?",
  "How do I improve my scholarship chances?",
  "Can we schedule a call?",
  "I need help with my personal statement",
  "Update me on Taylor's negotiation",
];

const BOT_REPLIES: Record<string, string> = {
  "What's my application status?": "Your Taylor's application has received an offer! 🎉 UCSI is under review, and IMU received your submission. I'm actively monitoring all of them.",
  "How do I improve my scholarship chances?": "Great question! Key tips: 1) Upload all documents fully 2) Write a compelling personal statement 3) Show extracurricular leadership. Let's schedule a session to review your personal statement together?",
  "Can we schedule a call?": "Absolutely! I have slots available this week on Thursday at 3PM and Friday at 11AM. Which works better for you? I'll send a Google Meet link right away 📅",
  "I need help with my personal statement": "Of course! I've helped 100+ students craft compelling statements. Share a draft in Documents and I'll review it within 24 hours with detailed feedback ✍️",
  "Update me on Taylor's negotiation": "I spoke with their admissions team yesterday — they're willing to review for an additional 5% scholarship (bringing it to 15% total) if you submit your extracurricular certificates this week. Upload them to Documents ASAP! 💪",
};

function MessageBubble({ msg, isStudent }: { msg: ChatMessage; isStudent: boolean }) {
  return (
    <div className={`flex gap-3 ${isStudent ? "flex-row-reverse" : "flex-row"}`}>
      {!isStudent && (
        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-[16px] flex-shrink-0 mt-auto">
          {COUNSELOR.avatar}
        </div>
      )}
      <div className={`flex flex-col gap-1 max-w-[78%] ${isStudent ? "items-end" : "items-start"}`}>
        {msg.attachment && (
          <div className={`flex items-center gap-2.5 px-4 py-3 rounded-2xl border ${isStudent ? "bg-violet-50 border-violet-100 rounded-br-md" : "bg-slate-50 border-slate-100 rounded-bl-md"}`}>
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <FileText className="w-4 h-4 text-red-500" />
            </div>
            <div>
              <p className="text-[12px] text-slate-800" style={{ fontWeight: 600 }}>{msg.attachment.name}</p>
              <p className="text-[10px] text-slate-400" style={{ fontWeight: 400 }}>PDF Document</p>
            </div>
          </div>
        )}
        <div className={`px-4 py-3 rounded-2xl text-[13px] leading-relaxed ${
          isStudent
            ? "bg-gradient-to-br from-violet-600 to-purple-600 text-white rounded-br-md"
            : "bg-white border border-slate-100 text-slate-700 rounded-bl-md shadow-sm"
        }`} style={{ fontWeight: 400 }}>
          {msg.text}
        </div>
        <div className={`flex items-center gap-1 ${isStudent ? "flex-row-reverse" : "flex-row"}`}>
          <span className="text-[10px] text-slate-400" style={{ fontWeight: 400 }}>{msg.time}</span>
          {isStudent && (
            msg.read ? <CheckCheck className="w-3 h-3 text-violet-400" /> : <Clock className="w-3 h-3 text-slate-300" />
          )}
        </div>
      </div>
    </div>
  );
}

function DateSeparator({ date }: { date: string }) {
  return (
    <div className="flex items-center gap-3 my-2">
      <div className="flex-1 h-px bg-slate-100" />
      <span className="text-[11px] text-slate-400 px-2" style={{ fontWeight: 500 }}>{date}</span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

export default function ChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>(MOCK_MESSAGES);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const getTime = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const newMsg: ChatMessage = { id: `msg_${Date.now()}`, from: "student", text, time: getTime(), date: "Today", read: false };
    setMessages(prev => [...prev, newMsg]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      const reply = BOT_REPLIES[text] || "Got it! Let me look into this and get back to you shortly. You can also check your Applications tab for real-time updates 😊";
      setMessages(prev => [...prev, { id: `msg_${Date.now() + 1}`, from: "counselor", text: reply, time: getTime(), date: "Today", read: false }]);
      setTyping(false);
    }, 1500);
  };

  // Group messages by date
  const groupedDates = [...new Set(messages.map(m => m.date))];

  return (
    <div className="flex flex-col h-screen lg:h-[calc(100vh-0px)] max-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-4 lg:px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-violet-100 flex items-center justify-center text-[20px]">
              {COUNSELOR.avatar}
            </div>
            <div>
              <p className="text-[14px] text-slate-900" style={{ fontWeight: 700 }}>{COUNSELOR.nickname}</p>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <p className="text-[11px] text-slate-500" style={{ fontWeight: 400 }}>Online · {COUNSELOR.responseTime}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center hover:bg-violet-50 hover:border-violet-100 hover:text-violet-600 transition-all text-slate-500">
              <Phone className="w-4 h-4" />
            </button>
            <button className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center hover:bg-violet-50 hover:border-violet-100 hover:text-violet-600 transition-all text-slate-500">
              <Video className="w-4 h-4" />
            </button>
            <button className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center hover:bg-slate-100 transition-all text-slate-500">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Messages area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Scrollable messages */}
          <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-5 bg-[#F8F8FB] flex flex-col gap-3">
            {/* Counselor intro card */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mx-auto bg-white rounded-3xl border border-slate-100 shadow-sm p-5 max-w-sm text-center mb-4">
              <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center text-[28px] mx-auto mb-3">
                {COUNSELOR.avatar}
              </div>
              <p className="text-[15px] text-slate-900 mb-0.5" style={{ fontWeight: 700 }}>{COUNSELOR.name}</p>
              <p className="text-[12px] text-slate-500 mb-3" style={{ fontWeight: 400 }}>{COUNSELOR.title} · {COUNSELOR.specialization}</p>
              <div className="flex justify-center gap-4">
                {[
                  { icon: <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />, value: `${COUNSELOR.rating} rating` },
                  { icon: <span className="text-[12px]">🎓</span>, value: COUNSELOR.universities },
                ].map((stat, i) => (
                  <div key={i} className="flex items-center gap-1">
                    {stat.icon}
                    <span className="text-[11px] text-slate-600" style={{ fontWeight: 500 }}>{stat.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {groupedDates.map(date => (
              <div key={date}>
                <DateSeparator date={date} />
                {messages.filter(m => m.date === date).map((msg, i) => (
                  <motion.div key={msg.id} initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.25 }} className="mb-2">
                    <MessageBubble msg={msg} isStudent={msg.from === "student"} />
                  </motion.div>
                ))}
              </div>
            ))}

            {/* Typing indicator */}
            <AnimatePresence>
              {typing && (
                <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="flex gap-3 items-end">
                  <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-[16px]">{COUNSELOR.avatar}</div>
                  <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex gap-1 items-center h-4">
                      {[0, 1, 2].map(i => (
                        <div key={i} className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={bottomRef} />
          </div>

          {/* Quick replies */}
          <div className="px-4 py-2.5 bg-white border-t border-slate-50 flex gap-2 overflow-x-auto scrollbar-hide flex-shrink-0">
            {QUICK_REPLIES.map((reply, i) => (
              <button key={i} onClick={() => sendMessage(reply)}
                className="flex-shrink-0 px-3.5 py-2 rounded-full text-[12px] text-violet-700 bg-violet-50 border border-violet-100 hover:bg-violet-100 transition-colors"
                style={{ fontFamily: "Inter, sans-serif", fontWeight: 500 }}>
                {reply}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-4 lg:px-6 py-3 bg-white border-t border-slate-100 flex items-center gap-3 flex-shrink-0">
            <button className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 hover:border-violet-100 transition-all flex-shrink-0">
              <Paperclip className="w-4 h-4" />
            </button>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMessage(input)}
              placeholder="Message Kak Sarah..."
              className="flex-1 px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-100 text-[13px] text-slate-700 placeholder-slate-400 outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-50 transition-all"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
            />
            <button onClick={() => sendMessage(input)}
              disabled={!input.trim()}
              className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-white shadow-md hover:shadow-lg hover:-translate-y-px transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right panel — counselor info (desktop only) */}
        <div className="hidden xl:flex w-72 bg-white border-l border-slate-100 flex-col">
          <div className="p-5 border-b border-slate-100">
            <p className="text-[12px] text-slate-500 uppercase tracking-widest mb-4" style={{ fontWeight: 700 }}>Your Counselor</p>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center text-[32px] mx-auto mb-3">
                {COUNSELOR.avatar}
              </div>
              <p className="text-[14px] text-slate-900 mb-0.5" style={{ fontWeight: 700 }}>{COUNSELOR.name}</p>
              <p className="text-[11px] text-slate-500 mb-1" style={{ fontWeight: 400 }}>{COUNSELOR.title}</p>
              <p className="text-[11px] text-violet-600 mb-4" style={{ fontWeight: 600 }}>{COUNSELOR.specialization}</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Rating", value: `⭐ ${COUNSELOR.rating}` },
                  { label: "Students", value: "120+" },
                  { label: "Experience", value: "8 yrs" },
                  { label: "Response", value: "~2 min" },
                ].map(stat => (
                  <div key={stat.label} className="bg-slate-50 rounded-xl p-2.5 text-center">
                    <p className="text-[13px] text-slate-900" style={{ fontWeight: 700 }}>{stat.value}</p>
                    <p className="text-[10px] text-slate-500" style={{ fontWeight: 500 }}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="p-5 flex flex-col gap-3">
            <p className="text-[12px] text-slate-500 uppercase tracking-widest" style={{ fontWeight: 700 }}>Quick Actions</p>
            {[
              { icon: <CalendarPlus className="w-4 h-4" />, label: "Book a Session", color: "text-violet-700 bg-violet-50 border-violet-100" },
              { icon: <Phone className="w-4 h-4" />, label: "Schedule a Call", color: "text-cyan-700 bg-cyan-50 border-cyan-100" },
              { icon: <FileText className="w-4 h-4" />, label: "Request Review", color: "text-orange-700 bg-orange-50 border-orange-100" },
            ].map((action, i) => (
              <button key={i} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-[13px] transition-all hover:-translate-y-px hover:shadow-sm ${action.color}`} style={{ fontWeight: 600 }}>
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>

          <div className="p-5 border-t border-slate-100 mt-auto">
            <p className="text-[12px] text-slate-500 uppercase tracking-widest mb-3" style={{ fontWeight: 700 }}>Your Journey</p>
            <div className="flex flex-col gap-2.5">
              {[
                { label: "Applications submitted", value: "3" },
                { label: "Offers received", value: "1" },
                { label: "Documents uploaded", value: "8/10" },
                { label: "Days with SCS Education", value: "72" },
              ].map((stat, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-[12px] text-slate-500" style={{ fontWeight: 400 }}>{stat.label}</span>
                  <span className="text-[13px] text-slate-900" style={{ fontWeight: 700 }}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
